import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import { apiRequest } from '../../../api/client';
import { postPreviewHtml } from '../../../api/pdfPreview';
import { defaultPdfCustomLayout, logoXFracFromAlign } from '../../../pdf/pdfCustomLayout';
import {
  type LayoutPatch,
  type OverrideFieldPath,
  type PdfDocType,
  type PdfStudioBranding,
  clearOverrideField,
  downloadFile,
  exportStudioConfig,
  layoutToJsonString,
  mergeLayoutPatches,
  mergePdfLayoutPartial,
  parseStudioImport,
  readDocOverride,
  readGlobalLayout
} from '../../../pdf/pdfStudioState';
import PdfStudioControls from './PdfStudioControls';
import PdfStudioPreview from './PdfStudioPreview';
import './pdf-studio.css';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const DOC_TITLES: Record<PdfDocType, string> = {
  quote: 'Quote PDF',
  invoice: 'Invoice PDF',
  inspection: 'Inspection Report PDF'
};

export type StudioSectionId =
  | 'section-info'
  | 'section-header'
  | 'section-title'
  | 'section-body'
  | 'section-footer'
  | 'section-overrides'
  | 'section-custom-html';

export const NAV_SECTIONS: ReadonlyArray<{
  id: StudioSectionId;
  label: string;
  hint: string;
  className: string;
}> = [
  {
    id: 'section-info',
    label: 'Document Info',
    hint: 'Company contact details, template & brand color',
    className: 'pdf-studio-nav-link--info'
  },
  {
    id: 'section-header',
    label: 'Header',
    hint: 'Logo, badges, header background & stripe',
    className: 'pdf-studio-nav-link--header'
  },
  {
    id: 'section-title',
    label: 'Title Row',
    hint: 'Document title size & color, company column',
    className: 'pdf-studio-nav-link--title'
  },
  {
    id: 'section-body',
    label: 'Body & Table',
    hint: 'Fonts, table colors, columns, and body sections',
    className: 'pdf-studio-nav-link--body'
  },
  {
    id: 'section-footer',
    label: 'Footer',
    hint: 'Disclaimer, compliance, footer logo strip',
    className: 'pdf-studio-nav-link--footer'
  },
  {
    id: 'section-overrides',
    label: 'Per-Doc Overrides',
    hint: 'Settings that apply only to the active doc type',
    className: 'pdf-studio-nav-link--overrides'
  },
  {
    id: 'section-custom-html',
    label: 'Custom HTML',
    hint: 'Bring your own HTML/CSS layout',
    className: 'pdf-studio-nav-link--custom'
  }
];

function buildPreviewPayload(
  branding: PdfStudioBranding,
  globalLayout: ReturnType<typeof readGlobalLayout>,
  overrides: NonNullable<PdfStudioBranding['pdfLayoutJsonOverrides']>,
  docType: PdfDocType
) {
  return {
    type: docType,
    branding: {
      ...branding,
      pdfLayoutJson: layoutToJsonString(globalLayout),
      pdfLayoutJsonOverrides: overrides
    },
    pdfLayoutJson: layoutToJsonString(mergePdfLayoutPartial(globalLayout, overrides[docType])),
    pdfLayoutJsonOverrides: overrides
  };
}

export default function PdfStudioPage(): React.JSX.Element {
  const [docType, setDocType] = useState<PdfDocType>('quote');
  const [activeSection, setActiveSection] = useState<StudioSectionId>('section-info');
  const [branding, setBranding] = useState<PdfStudioBranding>({});
  const [globalLayout, setGlobalLayout] = useState(() => defaultPdfCustomLayout('#2d8dcc'));
  const [overrides, setOverrides] = useState<NonNullable<PdfStudioBranding['pdfLayoutJsonOverrides']>>({});
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewRefreshing, setPreviewRefreshing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewGenRef = useRef(0);
  const [importMessage, setImportMessage] = useState<string | null>(null);

  const legacyLocked = branding.useClientRasterExport === true;
  const downloadTemplate = (branding.pdfTemplate || 'generic').toLowerCase();
  const previewMismatch =
    !legacyLocked && downloadTemplate === 'generic' && branding.pdfTemplate !== 'zera';

  const overridePartial = readDocOverride({ pdfLayoutJsonOverrides: overrides }, docType);

  const refreshPreview = useCallback(async () => {
    const gen = ++previewGenRef.current;
    setPreviewRefreshing(true);
    setPreviewError(null);
    try {
      const payload = buildPreviewPayload(branding, globalLayout, overrides, docType);
      const html = await postPreviewHtml(payload);
      if (gen !== previewGenRef.current) return;
      setPreviewHtml(html);
    } catch (e: unknown) {
      if (gen !== previewGenRef.current) return;
      setPreviewError(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      if (gen === previewGenRef.current) setPreviewRefreshing(false);
    }
  }, [branding, globalLayout, overrides, docType]);

  useEffect(() => {
    void (async () => {
      try {
        const res = await apiRequest<{ branding: PdfStudioBranding | null }>('/api/branding');
        const b = res.branding || {};
        setBranding(b);
        setGlobalLayout(readGlobalLayout(b));
        setOverrides(b.pdfLayoutJsonOverrides || {});
      } catch (e: unknown) {
        setPreviewError(e instanceof Error ? e.message : 'Failed to load branding');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (loading) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPreviewRefreshing(true);
    debounceRef.current = setTimeout(() => {
      void refreshPreview();
    }, 600);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [branding, globalLayout, overrides, docType, loading, refreshPreview]);

  const patchGlobal = useCallback(
    (patch: LayoutPatch) => {
      if (legacyLocked) return;
      setGlobalLayout((g) => mergePdfLayoutPartial(g, patch));
    },
    [legacyLocked]
  );

  const patchOverride = useCallback(
    (patch: LayoutPatch) => {
      if (legacyLocked) return;
      setOverrides((o) => ({
        ...o,
        [docType]: mergeLayoutPatches(o[docType], patch)
      }));
    },
    [legacyLocked, docType]
  );

  const resetOverrideField = useCallback(
    (path: OverrideFieldPath) => {
      if (legacyLocked) return;
      setOverrides((o) => clearOverrideField(o, docType, path));
    },
    [legacyLocked, docType]
  );

  function handleExportConfig(includeLogos: boolean) {
    const snapshot = exportStudioConfig(
      { ...branding, pdfLayoutJson: layoutToJsonString(globalLayout), pdfLayoutJsonOverrides: overrides },
      { includeLogos }
    );
    const filename = `pdf-studio-${branding.companyName ? branding.companyName.replace(/[^a-z0-9-]+/gi, '_').toLowerCase() : 'config'}-${new Date().toISOString().slice(0, 10)}.json`;
    downloadFile(filename, JSON.stringify(snapshot, null, 2));
  }

  async function handleImportConfig(file: File) {
    setImportMessage(null);
    try {
      const text = await file.text();
      const imported = parseStudioImport(text);
      if (!imported) {
        setImportMessage('Could not import — file is not a valid PDF Studio export.');
        setTimeout(() => setImportMessage(null), 5000);
        return;
      }
      setBranding((b) => ({ ...b, ...imported }));
      if (imported.pdfLayoutJson) setGlobalLayout(readGlobalLayout(imported));
      if (imported.pdfLayoutJsonOverrides) setOverrides(imported.pdfLayoutJsonOverrides);
      setImportMessage('Imported. Review and click Save to persist.');
      setTimeout(() => setImportMessage(null), 6000);
    } catch (e: unknown) {
      setImportMessage(e instanceof Error ? `Import failed: ${e.message}` : 'Import failed.');
    }
  }

  async function handleSave() {
    setSaveState('saving');
    setSaveError(null);
    setSaveNotice(null);
    try {
      const storedTemplate = branding.pdfTemplate || 'generic';
      const usePremiumForDownloads =
        !legacyLocked && storedTemplate !== 'zera' && storedTemplate !== 'premium';
      const payload: Record<string, unknown> = {
        ...branding,
        pdfTemplate: legacyLocked ? storedTemplate : usePremiumForDownloads ? 'premium' : storedTemplate
      };
      if (!legacyLocked) {
        payload.pdfLayoutJson = layoutToJsonString(globalLayout);
        payload.pdfLayoutJsonOverrides = overrides;
      }
      const res = await apiRequest<{ branding: PdfStudioBranding }>('/api/branding', {
        method: 'PUT',
        json: payload
      });
      setBranding(res.branding);
      setSaveState('saved');
      if (usePremiumForDownloads) {
        setSaveNotice('Saved. PDF template set to Premium so downloads match your Studio layout.');
      }
      setTimeout(() => {
        setSaveState('idle');
        setSaveNotice(null);
      }, 5000);
    } catch (e: unknown) {
      setSaveState('error');
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    }
  }

  function focusSection(id: StudioSectionId) {
    setActiveSection(id);
    // Scroll the right-panel controls back to the top so the user starts at the top of the section.
    requestAnimationFrame(() => {
      const right = document.querySelector('.pdf-studio-right') as HTMLElement | null;
      right?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  const saveLabel =
    saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved ✓' : 'Save';

  return (
    <AppShell>
      <div className="pdf-studio-page">
        <header className="pdf-studio-topbar">
          <div>
            <h1 className="pdf-studio-topbar-title">{DOC_TITLES[docType]}</h1>
            <p className="pdf-studio-topbar-sub">PDF Studio — layout & branding</p>
          </div>
          <div className="pdf-studio-topbar-actions">
            <Link to="/admin/settings" className="btn-secondary">
              ← Settings
            </Link>
            <div className="pdf-studio-io">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => handleExportConfig(false)}
                title="Download current Studio configuration as JSON (without uploaded logos)"
              >
                Export
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => handleExportConfig(true)}
                title="Export including logo images"
              >
                Export + logos
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => importInputRef.current?.click()}
                title="Import a previously exported Studio JSON configuration"
              >
                Import…
              </button>
              <input
                ref={importInputRef}
                type="file"
                accept="application/json,.json"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleImportConfig(f);
                  e.target.value = '';
                }}
              />
            </div>
            <div className="pdf-studio-save-wrap">
              <button
                type="button"
                className={`btn-primary pdf-studio-btn-save${saveState === 'saving' ? ' is-saving' : ''}${saveState === 'saved' ? ' is-saved' : ''}`}
                disabled={saveState === 'saving'}
                onClick={() => void handleSave()}
              >
                {saveLabel}
              </button>
              {saveState === 'error' && saveError ? (
                <p className="pdf-studio-save-error" role="alert">
                  {saveError}
                </p>
              ) : null}
              {saveNotice ? (
                <p className="pdf-studio-save-notice" role="status">
                  {saveNotice}
                </p>
              ) : null}
              {importMessage ? (
                <p className="pdf-studio-import-message" role="status">
                  {importMessage}
                </p>
              ) : null}
            </div>
          </div>
        </header>

        {legacyLocked ? (
          <p className="pdf-studio-legacy-banner" role="status">
            This workspace uses a legacy PDF layout. Layout editing is disabled to preserve your
            current document design.
          </p>
        ) : null}

        {previewMismatch ? (
          <p className="pdf-studio-template-banner" role="status">
            Your PDF template is <strong>Generic</strong>, so downloaded PDFs will not match this
            preview until you save (we will switch to <strong>Premium</strong> automatically) or
            choose Premium under Document Info.
          </p>
        ) : null}

        <div className="pdf-studio-grid">
          <aside className="pdf-studio-left">
            {loading ? (
              <>
                <div className="pdf-studio-skeleton pdf-studio-skeleton-controls" />
                <div className="pdf-studio-skeleton pdf-studio-skeleton-controls" />
                <div className="pdf-studio-skeleton pdf-studio-skeleton-controls" />
              </>
            ) : (
              <>
                <p className="pdf-studio-left-label">Document</p>
                <nav className="pdf-studio-tabs" aria-label="Document type">
                  {(
                    [
                      ['quote', 'Quotes'],
                      ['invoice', 'Invoices'],
                      ['inspection', 'Inspections']
                    ] as const
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      className={`pdf-studio-tab${docType === id ? ' is-active' : ''}`}
                      onClick={() => setDocType(id)}
                    >
                      {label}
                    </button>
                  ))}
                </nav>
                <p className="pdf-studio-left-label">Edit section</p>
                <nav className="pdf-studio-nav" aria-label="Section navigation">
                  {NAV_SECTIONS.map((s) => {
                    const isActive = activeSection === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className={`pdf-studio-nav-link ${s.className}${isActive ? ' is-active' : ''}`}
                        aria-pressed={isActive}
                        onClick={() => focusSection(s.id)}
                      >
                        <span className="pdf-studio-nav-link-label">{s.label}</span>
                        <span className="pdf-studio-nav-link-hint">{s.hint}</span>
                      </button>
                    );
                  })}
                </nav>
              </>
            )}
          </aside>

          {loading ? (
            <main className="pdf-studio-center">
              <div className="pdf-studio-skeleton pdf-studio-skeleton-preview" />
            </main>
          ) : (
            <PdfStudioPreview
              previewHtml={previewHtml}
              refreshing={previewRefreshing}
              error={previewError}
              showLogoPrompt={!branding.logoBase64?.trim()}
              onRetry={() => void refreshPreview()}
              onPreviewReady={() => setPreviewRefreshing(false)}
              onUploadLogoClick={() => {
                focusSection('section-header');
                logoInputRef.current?.click();
              }}
            />
          )}

          {loading ? (
            <aside className="pdf-studio-right">
              <div className="pdf-studio-controls">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="pdf-studio-skeleton pdf-studio-skeleton-controls" />
                ))}
              </div>
            </aside>
          ) : (
            <PdfStudioControls
              ref={logoInputRef}
              docType={docType}
              activeSection={activeSection}
              branding={branding}
              globalLayout={globalLayout}
              overridePatch={overridePartial}
              legacyLocked={legacyLocked}
              onBrandingChange={(patch) => setBranding((b) => ({ ...b, ...patch }))}
              onPatchGlobal={patchGlobal}
              onPatchOverride={patchOverride}
              onResetOverride={resetOverrideField}
              onLogoAlign={(align) => {
                const slotWidthFrac = globalLayout.logo.slotWidthFrac;
                patchGlobal({
                  logo: {
                    align,
                    slotWidthFrac,
                    xFrac: logoXFracFromAlign(align, slotWidthFrac),
                    yFrac: globalLayout.logo.yFrac
                  }
                });
              }}
            />
          )}
        </div>
      </div>
    </AppShell>
  );
}
